import { Turn, TurnType } from '@db8/shared'
import { useHallUsers } from '@/contexts/HallUsersProvider'
import { UserSelector } from './user-selector'
import { UserTurn } from './UserTurn'
import { CrossfireControls } from './CrossfireControls'
import { TimerDisplayServer } from './timer-display-server'
import { useTeamColors } from '@/hooks/useTeamColors'
import ProfileImage from '../ProfileImage'
import { useHall } from '@/contexts/HallProvider'

export const SequenceItem = ({
  turn,
  isSelectDisabled,
}: {
  turn: Turn
  isSelectDisabled?: boolean
}) => {
  const { hallMetadata } = useHall()
  const { getUser, isModerator, isBot } = useHallUsers()
  const { borderColor } = useTeamColors({ turnType: turn.type })

  const primaryUser = getUser(turn.primarySpeakerId)
  const secondaryUser = getUser(turn.secondarySpeakerId)

  const isCrossfire =
    turn.type === TurnType.AffirmativeCrossNegative || turn.type === TurnType.NegativeCrossAffirmative
  const hasTwoSlots = Boolean(turn.secondarySpeakerId)
  const crossfireHasBot =
    isCrossfire && hasTwoSlots && ((primaryUser && isBot(primaryUser)) || (secondaryUser && isBot(secondaryUser)))

  const NameComp = () => {
    if (!primaryUser && !secondaryUser) return null

    const renderUser = (user: typeof primaryUser) => (
      <div className="flex items-center gap-1">
        <ProfileImage user={user} className="w-5 h-5 rounded-full" />
        <span className="font-semibold">{user.displayName}</span>
      </div>
    )

    if (primaryUser && secondaryUser) {
      const left = turn.type === TurnType.NegativeCrossAffirmative ? secondaryUser : primaryUser
      const right = turn.type === TurnType.NegativeCrossAffirmative ? primaryUser : secondaryUser

      return (
        <div className="flex items-center gap-2 text-xs">
          {renderUser(left)}
          <span className="text-gray-400">vs</span>
          {renderUser(right)}
        </div>
      )
    }

    return (
      <div className="flex items-center gap-1 text-xs">
        {primaryUser && renderUser(primaryUser)}
        {secondaryUser && renderUser(secondaryUser)}
      </div>
    )
  }

  let emoji = ''
  if (turn.type === TurnType.Affirmative) emoji = '🟢'
  else if (turn.type === TurnType.Negative) emoji = '🔵'
  else if (turn.type === TurnType.AffirmativeCrossNegative) emoji = '🟢 x 🔵'
  else if (turn.type === TurnType.NegativeCrossAffirmative) emoji = '🔵 x 🟢'
  else if (turn.type === TurnType.Preperatory) emoji = '⌛'

  return (
    <li
      className={`border my-1 p-2 rounded-sm w-auto`}
      id={`turn-${turn.step}`}
      style={{
        borderColor: hallMetadata.activeTurn === turn.step && borderColor,
      }}
    >
      <div>
        <div className="flex justify-between items-center gap-0.5 font-semibold text-sm">
          <div>
            {turn.title} {emoji}
          </div>
          <div className="whitespace-nowrap text-xs">
            {turn.duration} min{turn.duration > 1 && 's'}
          </div>
        </div>

        <div
          className={`flex ${
            isModerator ? 'justify-end' : 'justify-between'
          } items-center gap-2 pt-1`}
        >
          {!isModerator && <NameComp />}
          {hallMetadata.activeTurn === turn.step && <TimerDisplayServer duration={turn.duration} />}
        </div>
      </div>

      {isModerator && (
        <div className="flex flex-col mt-2">
          <UserSelector turn={turn} isSelectDisabled={isSelectDisabled} />
          {hallMetadata.activeTurn === turn.step && (
            <>
              {crossfireHasBot ? (
                <CrossfireControls turn={turn} />
              ) : (
                <UserTurn
                  duration={turn.duration}
                  userId={turn.primarySpeakerId}
                  role={isBot(primaryUser) ? 'bot' : 'user'}
                  step={turn.step}
                />
              )}
            </>
          )}
        </div>
      )}
    </li>
  )
}
